import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import ReCAPTCHA from 'react-google-recaptcha';

// Icons
const ArrowRight = () => <span>→</span>;
const Mail = () => <span>✉️</span>;
const MapPin = () => <span>📍</span>;
const Github = () => <span>⚡</span>;
const Linkedin = () => <span>💼</span>;
const Twitter = () => <span>🐦</span>;
const CheckCircle = () => <span>✅</span>;
const AlertCircle = () => <span>❌</span>;
const Loader = () => <span>⏳</span>;

const ContactSection = () => {
  const form = useRef();
  const recaptchaRef = useRef();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [formState, setFormState] = useState({
    isSubmitting: false,
    submitStatus: null, // 'success', 'error', null
    errors: {},
    recaptchaToken: null
  });

  // Validation rules
  const validateForm = () => {
    const errors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Subject validation
    if (!formData.subject.trim()) {
      errors.subject = 'Subject is required';
    } else if (formData.subject.trim().length < 5) {
      errors.subject = 'Subject must be at least 5 characters';
    }

    // Message validation
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters';
    }

    // reCAPTCHA validation
    if (!formState.recaptchaToken) {
      errors.recaptcha = 'Please complete the reCAPTCHA verification';
    }

    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear specific field error when user starts typing
    if (formState.errors[name]) {
      setFormState(prev => ({
        ...prev,
        errors: {
          ...prev.errors,
          [name]: undefined
        }
      }));
    }
  };

  const handleRecaptchaChange = (token) => {
    setFormState(prev => ({
      ...prev,
      recaptchaToken: token,
      errors: {
        ...prev.errors,
        recaptcha: undefined
      }
    }));
  };

  const handleRecaptchaExpired = () => {
    setFormState(prev => ({
      ...prev,
      recaptchaToken: null
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
    setFormState({
      isSubmitting: false,
      submitStatus: null,
      errors: {},
      recaptchaToken: null
    });
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormState(prev => ({
        ...prev,
        errors,
        submitStatus: 'error'
      }));
      return;
    }

    // Set loading state
    setFormState(prev => ({
      ...prev,
      isSubmitting: true,
      submitStatus: null,
      errors: {}
    }));

    try {
      // Prepare template parameters for EmailJS
      const templateParams = {
        from_name: formData.name.trim(),
        from_email: formData.email.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        to_name: 'Chhatrapal Choudhary', // Your name
        reply_to: formData.email.trim(),
        'g-recaptcha-response': formState.recaptchaToken
      };

      // Send email using EmailJS
      const result = await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      console.log('Email sent successfully:', result);

      // Success state
      setFormState(prev => ({
        ...prev,
        isSubmitting: false,
        submitStatus: 'success'
      }));

      // Reset form after 3 seconds
      setTimeout(() => {
        resetForm();
      }, 3000);

    } catch (error) {
      console.error('Email sending failed:', error);
      
      // Error state
      setFormState(prev => ({
        ...prev,
        isSubmitting: false,
        submitStatus: 'error',
        errors: {
          submit: getErrorMessage(error)
        }
      }));
    }
  };

  const getErrorMessage = (error) => {
    if (error.text) {
      switch (error.text) {
        case 'The user does not exist':
          return 'Email service configuration error. Please try again later.';
        case 'Template not found':
          return 'Email template error. Please try again later.';
        case 'Service ID not found':
          return 'Email service error. Please try again later.';
        default:
          return `Email sending failed: ${error.text}`;
      }
    }
    return 'Something went wrong. Please try again later or contact me directly via email.';
  };

  const getSubmitButtonContent = () => {
    if (formState.isSubmitting) {
      return (
        <>
          <Loader />
          <span>Sending...</span>
        </>
      );
    }
    
    if (formState.submitStatus === 'success') {
      return (
        <>
          <CheckCircle />
          <span>Message Sent!</span>
        </>
      );
    }
    
    return (
      <>
        <span>Send Message</span>
        <ArrowRight />
      </>
    );
  };

  const getSubmitButtonClass = () => {
    let baseClass = 'btn btn--full';
    
    if (formState.isSubmitting) {
      return `${baseClass} btn--disabled`;
    }
    
    if (formState.submitStatus === 'success') {
      return `${baseClass} btn--success`;
    }
    
    if (formState.submitStatus === 'error') {
      return `${baseClass} btn--error`;
    }
    
    return `${baseClass} btn--primary`;
  };

  return (
    <section className="contact">
      <div className="container">
        <div className="contact__content">
          <div className="contact__info">
            <div className="section__badge">
              <span>📬</span>
              <span>Get In Touch</span>
            </div>
            
            <h2 className="section__title">
              Let's Create Something <span className="text--accent">Amazing</span> Together
            </h2>
            
            <p className="contact__description">
              Ready to bring your ideas to life? Whether you have a project in mind or just want to chat about technology, 
              I'm always excited to connect with fellow innovators and creators.
            </p>
            
            <div className="contact__methods">
              <div className="contact__method">
                <div className="method__icon">
                  <Mail />
                </div>
                <div className="method__content">
                  <h4>Email</h4>
                  <p>chhatrapalchoudhary101@gmail.com</p>
                </div>
              </div>
              
              <div className="contact__method">
                <div className="method__icon">
                  <MapPin />
                </div>
                <div className="method__content">
                  <h4>Location</h4>
                  <p>Available Worldwide</p>
                </div>
              </div>
            </div>
            
            <div className="contact__social">
              <h4>Follow My Journey</h4>
              <div className="social__grid">
                <a href="https://github.com/chhatrapalchoudhaary" className="social__card" target="_blank" rel="noopener noreferrer">
                  <Github />
                  <span>GitHub</span>
                </a>
                <a href="https://www.linkedin.com/in/chhatrapal-choudhary-143a3881/" className="social__card" target="_blank" rel="noopener noreferrer">
                  <Linkedin />
                  <span>LinkedIn</span>
                </a>
                <a href="https://x.com/web_chhatrapal" className="social__card" target="_blank" rel="noopener noreferrer">
                  <Twitter />
                  <span>Twitter</span>
                </a>
              </div>
            </div>
          </div>
          
          <div className="contact__form">
            {/* Status Messages */}
            {formState.submitStatus === 'success' && (
              <div className="form__message form__message--success">
                <CheckCircle />
                <div>
                  <strong>Message sent successfully!</strong>
                  <p>Thank you for reaching out. I'll get back to you within 24 hours.</p>
                </div>
              </div>
            )}
            
            {formState.submitStatus === 'error' && formState.errors.submit && (
              <div className="form__message form__message--error">
                <AlertCircle />
                <div>
                  <strong>Failed to send message</strong>
                  <p>{formState.errors.submit}</p>
                </div>
              </div>
            )}

            <form className="form" ref={form} onSubmit={handleSubmit} noValidate>
              <div className="form__row">
                <div className="form__group">
                  <label htmlFor="name">Full Name *</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe" 
                    required
                    disabled={formState.isSubmitting}
                    className={formState.errors.name ? 'form__input--error' : ''}
                  />
                  {formState.errors.name && (
                    <span className="form__error">{formState.errors.name}</span>
                  )}
                </div>
                
                <div className="form__group">
                  <label htmlFor="email">Email Address *</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com" 
                    required
                    disabled={formState.isSubmitting}
                    className={formState.errors.email ? 'form__input--error' : ''}
                  />
                  {formState.errors.email && (
                    <span className="form__error">{formState.errors.email}</span>
                  )}
                </div>
              </div>
              
              <div className="form__group">
                <label htmlFor="subject">Subject *</label>
                <input 
                  type="text" 
                  id="subject" 
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Let's discuss your project" 
                  required
                  disabled={formState.isSubmitting}
                  className={formState.errors.subject ? 'form__input--error' : ''}
                />
                {formState.errors.subject && (
                  <span className="form__error">{formState.errors.subject}</span>
                )}
              </div>
              
              <div className="form__group">
                <label htmlFor="message">Message *</label>
                <textarea 
                  id="message" 
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={6} 
                  placeholder="Tell me about your project ideas and how I can help bring them to life..."
                  required
                  disabled={formState.isSubmitting}
                  className={formState.errors.message ? 'form__input--error' : ''}
                ></textarea>
                {formState.errors.message && (
                  <span className="form__error">{formState.errors.message}</span>
                )}
              </div>

              {/* reCAPTCHA */}
              <div className="form__group">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={handleRecaptchaChange}
                  onExpired={handleRecaptchaExpired}
                  theme="dark"
                />
                {formState.errors.recaptcha && (
                  <span className="form__error">{formState.errors.recaptcha}</span>
                )}
              </div>
              
              <button 
                type="submit" 
                className={getSubmitButtonClass()}
                disabled={formState.isSubmitting || formState.submitStatus === 'success'}
              >
                {getSubmitButtonContent()}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;